import React from 'react';
import { Typography, Space, Tag } from 'antd';
import { CameraOutlined, LinkOutlined, UserOutlined } from '@ant-design/icons';
import { NewsArticle } from '../../types/news';

const { Text } = Typography;

interface ImageAttributionProps {
    article: NewsArticle;
}

const ImageAttribution: React.FC<ImageAttributionProps> = ({ article }) => {
    if (!article.image_source || !article.image_source_url) {
        return null;
    }

    const getSourceColor = (source: string) => {
        switch (source) {
            case 'unsplash':
                return 'blue';
            case 'pexels':
                return 'green';
            case 'scraped':
                return 'orange';
            case 'manual':
                return 'purple';
            case 'generated':
                return 'cyan';
            default:
                return 'default';
        }
    };

    const getSourceLabel = (source: string) => {
        switch (source) {
            case 'unsplash':
                return 'Unsplash';
            case 'pexels':
                return 'Pexels';
            case 'scraped':
                return 'Scraped';
            case 'manual':
                return 'Manual';
            case 'generated':
                return 'AI Generated';
            default:
                return source;
        }
    };

    const getViewOriginalText = (source: string) => {
        switch (source) {
            case 'unsplash':
                return 'View on Unsplash';
            case 'pexels':
                return 'View on Pexels';
            default:
                return 'View Original';
        }
    };
    
    return (
        <div style={{ 
            marginTop: '8px', 
            padding: '12px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '6px',
            border: '1px solid #e9ecef'
        }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {/* Source attribution */}
                <Space size="small" align="center">
                    <Tag 
                        color={getSourceColor(article.image_source)} 
                        icon={<CameraOutlined />}
                        style={{ fontSize: '12px', fontWeight: '500' }}
                    >
                        {getSourceLabel(article.image_source)}
                    </Tag>
                </Space>
                
                {/* Photographer attribution */}
                {article.image_source_author && (
                    <Space size="small" align="center">
                        <UserOutlined style={{ color: '#666', fontSize: '12px' }} />
                        <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>
                            Photo by {article.image_source_author}
                        </Text>
                        {article.image_source_author_url && (
                            <a 
                                href={article.image_source_author_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    fontSize: '12px',
                                    color: '#1890ff',
                                    textDecoration: 'none'
                                }}
                            >
                                <LinkOutlined /> View Profile
                            </a>
                        )}
                    </Space>
                )}
                
                {/* Original image link */}
                {article.image_source_url && (
                    <Space size="small" align="center">
                        <a 
                            href={article.image_source_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ 
                                fontSize: '12px',
                                color: '#1890ff',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <LinkOutlined />
                            {getViewOriginalText(article.image_source)}
                        </a>
                    </Space>
                )}
            </Space>
        </div>
    );
};

export default ImageAttribution; 